import OfferPageStateProvider from 'contexts/OfferPage/offerPageStateProvider'
import { OfferPageState, OfferPageDispatchAction, OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageReducer from 'hooks/OfferPage/useOfferPageReducer'
import { useAppSelector } from 'hooks/reduxHooks'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { ReducerMiddleware } from 'lib/redux/reducerUtils'
import React, { useEffect, useMemo, useState } from 'react'
import { getInitialLeOfferPageState } from 'selectors/offerPage/initialOfferPageStateSelectors'
import accommodationMobileFilterMiddleware from 'components/OfferPage/Accommodation/AccommodationMobileFilterMiddleware'
import { getNextFilterWithCapacityCheckInMiddlware } from '../utils'
import FlashHotelContent from './FlashHotelContent'
import { useSyncHotelTripItem } from 'tripPlanner/hooks/bookmarks/useSyncHotelTripItem'
import useMountedEffect from 'hooks/useMountedEffect'
import { offerPageDurationChangeEvent, offerPageGuestSelectionEvent, offerPageHotLeadEvent } from 'analytics/snowplow/events'
import * as Analytics from 'analytics/analytics'
import { useSaveHotLead } from 'hooks/Recommendation/useSaveHotLead'
import { generateRoomOccupants } from 'lib/offer/occupancyUtils'
import multiRoomsMobileFilterMiddleware from '../MultipleRoomsBooking/Mobile/MobileMultipleRoomsFilterMiddleware'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import { EmptyArray } from 'lib/array/arrayUtils'

const mobileVariantMiddlewares: Array<ReducerMiddleware<OfferPageState, OfferPageDispatchAction>> = [accommodationMobileFilterMiddleware]
const mobileNewMultiRoomMiddlewares: Array<ReducerMiddleware<OfferPageState, OfferPageDispatchAction>> = [multiRoomsMobileFilterMiddleware]
const defaultMiddlewares: Array<ReducerMiddleware<OfferPageState, OfferPageDispatchAction>> = [getNextFilterWithCapacityCheckInMiddlware]
const defaultRooms = [generateRoomOccupants()]

interface Props {
  offer: App.HotelOffer;
}

function FlashHotelContainer(props: Props) {
  const { offer } = props
  const [middlewares, setMiddlewares] = useState(defaultMiddlewares)
  const defaults = useAppSelector(state => getInitialLeOfferPageState(state, offer))

  const [state, stateDispatch] = useOfferPageReducer({
    ...defaults,
    duration: defaults.duration ?? offer.lowestPricePackage?.duration,
    offer,
    flights: offer.bundledWithFlightsOnly,
  }, middlewares)

  const isMobileDevice = useScreenSizeOnly('mobile')
  const rooms = state.rooms ?? EmptyArray as Array<App.RoomOccupants>
  const isNewMobileMultipleRoomEnabled = !!useOptimizelyExperiment(OptimizelyFeatureFlags.croNewMobileMultipleRoom)
  const showNewMobileMultipleRoom = useMemo(() => isMobileDevice && isNewMobileMultipleRoomEnabled && rooms.length > 1, [isMobileDevice, isNewMobileMultipleRoomEnabled, rooms])

  useSyncHotelTripItem(state, offer)

  useEffect(() => {
    if (isMobileDevice) {
      if (showNewMobileMultipleRoom) {
        setMiddlewares(mobileNewMultiRoomMiddlewares)
      } else {
        setMiddlewares(mobileVariantMiddlewares)
      }
    } else {
      setMiddlewares(defaultMiddlewares)
    }
  }, [isMobileDevice, showNewMobileMultipleRoom])

  useMountedEffect(() => {
    // the first loaded offer from an SSR is a summary. We then fetch the full version
    // This ensures the offer page state has our latest version
    stateDispatch({ type: OfferPageActions.setOffer, offer })
  }, [offer])

  useMountedEffect(() => {
    Analytics.trackEvent(offerPageDurationChangeEvent(offer, state))
  }, [state.duration])

  useMountedEffect(() => {
    if (state.rooms) {
      Analytics.trackEvent(offerPageGuestSelectionEvent(offer, state))
    }
  }, [state.rooms])

  const hotLead = useSaveHotLead({
    offerId: offer.id,
    offerType: offer.type,
    checkInDate: state.checkInDate,
    duration: state.duration,
    occupancy: state.rooms,
    defaultOccupancy: defaultRooms,
  })

  useEffect(() => {
    if (hotLead?.checkIn && hotLead.checkOut) {
      Analytics.trackEvent(offerPageHotLeadEvent(offer, state))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotLead])

  return <OfferPageStateProvider state={state} dispatch={stateDispatch}>
    <FlashHotelContent
      offer={offer}
    />
  </OfferPageStateProvider>
}

export default React.memo(FlashHotelContainer)
